/**
 * The camera's depth range follows the ladder (WORKBOARD lane A, the vehicle camera path's first
 * bill; flagged by the style sheet, §10.1). The near plane was a fixed 0.1 u — fine for every
 * classic camera (the orbit is 65 u out, the over-the-shoulder boom 3.2 u), but at k_human = 1/20
 * the camera sits ~0.16 u behind a 0.055 u person and the near plane cuts them at the shoulder.
 *
 * Rule: near = a quarter of the camera's clearance above the nearest ground, clamped to
 * [NEAR_MIN, NEAR_MAX]. Every classic camera has ≥ 0.4 u of clearance, so it keeps exactly 0.1 —
 * the classic picture is unchanged. The far plane stays 500 u (the sky and the system must stay
 * drawn from the ground). At NEAR_MIN the 24-bit depth buffer still resolves ~1 mm at 10 u and
 * ~0.1 u at 100 u — enough for the ground, clouds (≥ 0.3 u above it) and distant planets; a
 * logarithmic depth buffer is the fallback if layer-0 play shows distant z-fighting (it costs every
 * custom shader a log-depth chunk, so not by default).
 */

import type * as THREE from 'three';
import type { PlanetState, Vec3 } from '../types';
import { getSurfaceRadius } from '../core/surface';

export const NEAR_MAX = 0.1;
export const NEAR_MIN = 0.002;
export const CAMERA_FAR = 500;

/** Distance from a camera position down to the nearest planet's actual ground (terrain under it); ≥ 0. */
export function cameraClearance(pos: Vec3, planets: readonly PlanetState[]): number {
  let best = Infinity;
  for (const p of planets) {
    if (p.alive === false) continue;
    const dx = pos.x - p.position.x, dy = pos.y - p.position.y, dz = pos.z - p.position.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    // cheap reject: the sphere surface is a lower bound on distance to any terrain inside 1.5 R
    if (d - p.radius * 1.5 > best) continue;
    const ground = d > 1e-9 && p.heightmap ? getSurfaceRadius(p, { x: dx / d, y: dy / d, z: dz / d }) : p.radius;
    best = Math.min(best, Math.max(0, d - ground));
  }
  return best;
}

/** Near plane for a clearance (see the file comment). */
export function nearPlaneFor(clearance: number): number {
  if (!Number.isFinite(clearance)) return NEAR_MAX;
  return Math.min(NEAR_MAX, Math.max(NEAR_MIN, clearance * 0.25));
}

/** 74.9: the far plane — the game's 500 u, or farther when a host asks (`camera.userData.farAtLeast`: the commander's system stop, where a megaplanet stands past 500 u). */
export function farPlaneFor(atLeast: number | undefined): number {
  return Number.isFinite(atLeast) && (atLeast as number) > CAMERA_FAR ? (atLeast as number) : CAMERA_FAR;
}

/** Set the camera's near plane for where it is; only re-projects when the plane moves by more than 5 %. */
export function fitCameraDepth(camera: THREE.PerspectiveCamera, planets: readonly PlanetState[]): void {
  // DECISIONS 207 (7): a host may hold the plane under a cap while its camera sits in something small (the ride-in cabin,
  // a few hundredths across, flown high: its clearance alone would put the plane past the far wall)
  const cap = camera.userData.nearMost as number | undefined;
  const near = Math.min(cap ?? NEAR_MAX, nearPlaneFor(cameraClearance(camera.position, planets)));
  const far = farPlaneFor(camera.userData.farAtLeast as number | undefined);
  if (Math.abs(near - camera.near) <= camera.near * 0.05 && camera.far === far) return;
  camera.near = near;
  camera.far = far;
  camera.updateProjectionMatrix();
}
